<script>
  import CityField from "@/components/molecules/CityField.vue";
  import OfficeField from "@/components/molecules/OfficeField.vue";
  import RoomField from "@/components/molecules/RoomField.vue";
  import PlatformTypeSelector from "@/components/molecules/PlatformTypeSelector.vue";
  import StarsRatingInput from "@/components/atoms/StarsRatingInput.vue";
  import AtomInput from "@/components/atoms/AtomInput.vue";

  import { mapActions, mapState } from "vuex";

  import { createCity, updateCity, setCityEnable, setCityOperation } from "@/services/citiesService";
  import { createOffice, updateOffice, setOfficeEnable, setOfficeOperation } from "@/services/officesService";
  import { createRoom, updateRoom, setRoomEnable, setRoomOperation } from "@/services/roomsService";

  export default {
    name: "LocationForm",
    components: { CityField, OfficeField, RoomField, PlatformTypeSelector, StarsRatingInput, AtomInput },

    data() {
      return {
        city: null,
        office: null,
        room: null,

        qualificationRoom: 0,
        platformType: null,

        cityEditor: { open: false, mode: "create", name: "" },
        officeEditor: { open: false, mode: "create", name: "" },
        roomEditor: { open: false, mode: "create", name: "", ip: "", platformType: null, qualification: 0 },

        cityBusy: false,
        officeBusy: false,
        roomBusy: false,

        citiesAll: [],
        officesAll: [],
        roomsAll: [],
      };
    },

    computed: {
      ...mapState({
        cities: (s) => s.cities.data || [],
        offices: (s) => s.offices.data || [],
        rooms: (s) => s.rooms.data || [],
      }),

      cityOptions() {
        return [{ value: "", label: "Seleccione..." }].concat(
          (this.citiesAll || []).map((c) => ({
            value: c._id,
            label: c.cityName,
          }))
        );
      },
      officeOptions() {
        if (!this.city?._id) return [{ value: "", label: "Seleccione..." }];
        const cid = String(this.city._id);
        return [{ value: "", label: "Seleccione..." }].concat((this.officesAll || []).filter((o) => String(o.cityId?._id || o.cityId) === cid).map((o) => ({ value: o._id, label: o.officeName })));
      },
      roomOptions() {
        if (!this.office?._id) return [{ value: "", label: "Seleccione..." }];
        const oid = String(this.office.officeName);
        return [{ value: "", label: "Seleccione..." }].concat((this.roomsAll || []).filter((r) => String(r.officeName) === oid).map((r) => ({ value: r._id, label: r.roomName })));
      },

      platformSubject() {
        return "monitor";
      },

      citiesLoaded() {
        return this.$store.state.cities.loaded;
      },
      officesLoaded() {
        return this.$store.state.offices.loaded;
      },
      roomsLoaded() {
        return this.$store.state.rooms.loaded;
      },
      locksOffices() {
        const c = this.city || {};
        const cityRetired = (c.cityStatus || "available") === "retired";
        return !!c && (!c.isEnable || cityRetired);
      },
      locksRooms() {
        const o = this.office || {};
        const officeRetired = (o.officeStatus || "available") === "retired";
        const officeLocked = !!o && (!o.isEnable || officeRetired);
        return this.locksOffices || officeLocked;
      },
    },

    methods: {
      ...mapActions("cities", { fetchCities: "fetch" }),
      ...mapActions("offices", { fetchOffices: "fetch" }),
      ...mapActions("rooms", { fetchRooms: "fetch" }),

      upsertById(list, item) {
        if (!item?._id) return Array.isArray(list) ? [...list] : [];
        const copy = Array.isArray(list) ? [...list] : [];
        const i = copy.findIndex((x) => x._id === item._id);
        if (i >= 0) copy.splice(i, 1, item);
        else copy.push(item);
        return copy;
      },

      getSwal() {
        return this.$swal || this.$Swal || window.Swal;
      },

      async mountedFetches() {
        try {
          const [cRes, oRes, rRes] = await Promise.allSettled([this.fetchCities("?all=true"), this.fetchOffices("all"), this.fetchRooms("?all=true")]);

          const pickArray = (settled, storeKey, responseKeys = []) => {
            if (settled.status === "fulfilled") {
              const v = settled.value;
              if (Array.isArray(v)) return v;
              for (const k of responseKeys) {
                const arr = v?.[k];
                if (Array.isArray(arr)) return arr;
              }
            }
            const fromStore = this.$store.state?.[storeKey]?.data;
            if (Array.isArray(fromStore)) return fromStore.slice();
            return [];
          };

          this.citiesAll = pickArray(cRes, "cities", ["data", "cities"]);
          this.officesAll = pickArray(oRes, "offices", ["data", "offices"]);
          this.roomsAll = pickArray(rRes, "rooms", ["data", "rooms"]);
        } catch (err) {
          console.error("[mountedFetches] ❌ Error general:", err);
          this.citiesAll = [];
          this.officesAll = [];
          this.roomsAll = [];
        }
      },

      async onCitySelect(opt) {
        const isObj = typeof opt === "object" && opt !== null;
        const id = isObj ? opt._id || opt.value : opt;
        const name = isObj ? opt.cityName || opt.label : null;

        this.city =
          (this.citiesAll || []).find((c) => c._id && c._id === id) ||
          (this.citiesAll || []).find((c) => (c._id || c.cityName) === id) ||
          (this.citiesAll || []).find((c) => c.cityName === name) ||
          null;
      },

      async onOfficeSelect(officeId) {
        this.office = (this.officesAll || []).find((o) => o._id === officeId) || null;
        this.room = null;
        this.platformType = null;
        this.qualificationRoom = 0;
      },

      onRoomSelect(roomId) {
        this.room = (this.roomsAll || []).find((r) => r._id === roomId) || null;
        this.platformType = this.room?.platformType || null;
        this.qualificationRoom = Number(this.room?.qualification) || 0;
      },

      async onCityEnable(v) {
        if (!this.city?._id || this.cityBusy) return;
        const prevEnabled = !!this.city?.isEnable;
        const Swal = this.getSwal();
        this.cityBusy = true;
        try {
          Swal?.fire({ title: "Actualizando ciudad...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
          const updated = await setCityEnable(this.city._id, v);
          const saved = updated?.city || updated;
          if (!saved?._id) throw new Error("La API no devolvió la ciudad actualizada.");
          this.$store.commit("cities/update", saved);
          this.citiesAll = this.upsertById(this.citiesAll, saved);
          this.city = saved;
          Swal?.close();
          Swal?.fire({ icon: "success", title: "Listo", text: `Ciudad ${v ? "habilitada" : "inhabilitada"} correctamente.`, confirmButtonColor: "#bd0909" });
        } catch (err) {
          const rolled = { ...(this.city || {}), isEnable: prevEnabled };
          this.$store.commit("cities/update", rolled);
          this.citiesAll = this.upsertById(this.citiesAll, rolled);
          this.city = rolled;
          Swal?.close();
          Swal?.fire({ icon: "error", title: "No se pudo actualizar", text: err?.response?.data?.error || err?.message || "Inténtalo nuevamente.", confirmButtonColor: "#bd0909" });
        } finally {
          this.cityBusy = false;
        }
      },

      async onCityOperation(v) {
        if (!this.city?._id || this.cityBusy) return;
        const Swal = this.getSwal();
        this.cityBusy = true;

        const prevStatus = this.city?.cityStatus || "available";
        const prevEnabled = !!this.city?.isEnable;

        try {
          Swal?.fire({ title: "Actualizando ciudad...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
          const updated = await setCityOperation(this.city._id, v);
          const saved = updated?.city || updated;
          if (!saved?._id) throw new Error("La API no devolvió la ciudad actualizada.");
          this.$store.commit("cities/update", saved);
          this.citiesAll = this.upsertById(this.citiesAll, saved);
          this.city = saved;
          Swal?.close();
          Swal?.fire({ icon: "success", title: "Listo", text: `Ciudad marcada como ${v ? "en operación" : "retirada"}.`, confirmButtonColor: "#bd0909" });
        } catch (err) {
          const rolled = { ...(this.city || {}), cityStatus: prevStatus, isEnable: prevEnabled };
          this.$store.commit("cities/update", rolled);
          this.citiesAll = this.upsertById(this.citiesAll, rolled);
          this.city = rolled;
          Swal?.close();
          Swal?.fire({ icon: "error", title: "No se pudo actualizar", text: err?.response?.data?.error || err?.message || "Inténtalo nuevamente.", confirmButtonColor: "#bd0909" });
        } finally {
          this.cityBusy = false;
        }
      },

      async onOfficeEnable(v) {
        if (!this.office?._id || this.officeBusy) return;
        const prevEnabled = !!this.office?.isEnable;
        const Swal = this.getSwal();
        this.officeBusy = true;
        try {
          Swal?.fire({ title: "Actualizando sede...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
          const updated = await setOfficeEnable(this.office._id, v);
          const saved = updated?.office || updated;
          if (!saved?._id) throw new Error("La API no devolvió la sede actualizada.");
          this.$store.commit("offices/update", saved);
          this.officesAll = this.upsertById(this.officesAll, saved);
          this.office = saved;
          Swal?.close();
          Swal?.fire({ icon: "success", title: "Listo", text: `Sede ${v ? "habilitada" : "inhabilitada"} correctamente.`, confirmButtonColor: "#bd0909" });
        } catch (err) {
          const rolled = { ...(this.office || {}), isEnable: prevEnabled };
          this.$store.commit("offices/update", rolled);
          this.officesAll = this.upsertById(this.officesAll, rolled);
          this.office = rolled;
          Swal?.close();
          Swal?.fire({ icon: "error", title: "No se pudo actualizar", text: err?.response?.data?.error || err?.message || "Inténtalo nuevamente.", confirmButtonColor: "#bd0909" });
        } finally {
          this.officeBusy = false;
        }
      },

      async onOfficeOperation(v) {
        if (!this.office?._id || this.officeBusy) return;
        const Swal = this.getSwal();
        this.officeBusy = true;

        const prevStatus = this.office?.officeStatus || "available";
        const prevEnabled = !!this.office?.isEnable;

        try {
          Swal.fire({ title: "Actualizando sede...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
          const updated = await setOfficeOperation(this.office._id, v);
          const saved = updated?.office || updated;
          if (!saved?._id) throw new Error("La API no devolvió la sede actualizada.");
          this.$store.commit("offices/update", saved);
          this.officesAll = this.upsertById(this.officesAll, saved);
          this.office = saved;
          Swal?.close();
          Swal?.fire({ icon: "success", title: "Listo", text: `Sede marcada como ${v ? "en operación" : "retirada"}.`, confirmButtonColor: "#bd0909" });
        } catch (err) {
          const rolled = { ...(this.office || {}), officeStatus: prevStatus, isEnable: prevEnabled };
          this.$store.commit("offices/update", rolled);
          this.officesAll = this.upsertById(this.officesAll, rolled);
          this.office = rolled;
          Swal?.close();
          Swal?.fire({ icon: "error", title: "No se pudo actualizar", text: err?.response?.data?.error || err?.message || "Inténtalo nuevamente.", confirmButtonColor: "#bd0909" });
        } finally {
          this.officeBusy = false;
        }
      },

      async onRoomEnable(v) {
        if (!this.room?._id || this.roomBusy) return;
        const prevEnabled = !!this.room?.isEnable;
        const Swal = this.getSwal();
        this.roomBusy = true;
        try {
          Swal?.fire({ title: "Actualizando habitación...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
          const updated = await setRoomEnable(this.room._id, v);
          const saved = updated?.room || updated;
          if (!saved?._id) throw new Error("La API no devolvió la habitación actualizada.");
          this.$store.commit("rooms/update", saved);
          this.roomsAll = this.upsertById(this.roomsAll, saved);
          this.room = saved;
          Swal?.close();
          Swal?.fire({ icon: "success", title: "Listo", text: `Habitación ${v ? "habilitada" : "inhabilitada"} correctamente.`, confirmButtonColor: "#bd0909" });
        } catch (err) {
          const rolled = { ...(this.room || {}), isEnable: prevEnabled };
          this.$store.commit("rooms/update", rolled);
          this.roomsAll = this.upsertById(this.roomsAll, rolled);
          this.room = rolled;
          Swal?.close();
          Swal?.fire({ icon: "error", title: "No se pudo actualizar", text: err?.response?.data?.error || err?.message || "Inténtalo nuevamente.", confirmButtonColor: "#bd0909" });
        } finally {
          this.roomBusy = false;
        }
      },

      async onRoomOperation(v) {
        if (!this.room?._id || this.roomBusy) return;
        const Swal = this.getSwal();
        this.roomBusy = true;

        const prevStatus = this.room?.roomStatus || "available";
        const prevEnabled = !!this.room?.isEnable;

        try {
          Swal?.fire({ title: "Actualizando habitación...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
          const updated = await setRoomOperation(this.room._id, v);
          const saved = updated?.room || updated;
          if (!saved?._id) throw new Error("La API no devolvió la habitación actualizada.");
          this.$store.commit("rooms/update", saved);
          this.roomsAll = this.upsertById(this.roomsAll, saved);
          this.room = saved;
          Swal?.close();
          Swal?.fire({ icon: "success", title: "Listo", text: `Habitación marcada como ${v ? "en operación" : "retirada"}.`, confirmButtonColor: "#bd0909" });
        } catch (err) {
          const rolled = { ...(this.room || {}), roomStatus: prevStatus, isEnable: prevEnabled };
          this.$store.commit("rooms/update", rolled);
          this.roomsAll = this.upsertById(this.roomsAll, rolled);
          this.room = rolled;
          Swal.close();
          Swal?.fire({ icon: "error", title: "No se pudo actualizar", text: err?.response?.data?.error || err?.message || "Inténtalo nuevamente.", confirmButtonColor: "#bd0909" });
        } finally {
          this.roomBusy = false;
        }
      },

      openCityCreate() {
        this.cityEditor = { open: true, mode: "create", name: "" };
      },
      openCityEdit() {
        this.cityEditor = { open: true, mode: "edit", name: this.city?.cityName || "" };
      },
      cancelCityEdit() {
        this.cityEditor.open = false;
      },
      async submitCity() {
        const name = (this.cityEditor.name || "").trim();
        if (!name) return;
        const Swal = this.getSwal();
        try {
          Swal?.fire({ title: this.cityEditor.mode === "create" ? "Creando ciudad..." : "Actualizando ciudad...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

          let res;
          if (this.cityEditor.mode === "create") {
            res = await createCity({ cityName: name });
          } else {
            if (!this.city?._id) throw new Error("No hay ciudad seleccionada para editar.");
            res = await updateCity(this.city._id, { cityName: name });
          }

          const saved = res?.city || res;
          if (!saved || !saved._id) throw new Error(res.error);

          if (this.cityEditor.mode === "create") {
            this.$store.commit("cities/add", saved);
          } else {
            this.$store.commit("cities/update", saved);
          }
          this.citiesAll = this.upsertById(this.citiesAll, saved);
          this.city = saved;
          this.cityEditor.open = false;

          Swal?.close();
          Swal?.fire({
            icon: "success",
            title: this.cityEditor.mode === "create" ? "Ciudad creada con éxito" : "Ciudad actualizada con éxito",
            text: "La ciudad se ha guardado correctamente.",
            confirmButtonColor: "#bd0909",
          });
        } catch (err) {
          Swal?.close();
          Swal?.fire({ icon: "error", title: "No se pudo guardar", text: err?.response?.data?.error || err?.message || "Inténtalo nuevamente.", confirmButtonColor: "#bd0909" });
        }
      },

      openOfficeCreate() {
        this.officeEditor = { open: true, mode: "create", name: "" };
      },
      openOfficeEdit() {
        this.officeEditor = { open: true, mode: "edit", name: this.office?.officeName || "" };
      },
      cancelOfficeEdit() {
        this.officeEditor.open = false;
      },
      async submitOffice() {
        if (!this.city?._id) return;
        const name = (this.officeEditor.name || "").trim();
        if (!name) return;

        const Swal = this.getSwal();
        try {
          Swal?.fire({ title: this.officeEditor.mode === "create" ? "Creando sede..." : "Actualizando sede...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

          let res;
          if (this.officeEditor.mode === "create") {
            res = await createOffice({ cityId: this.city._id, cityName: this.city.cityName, officeName: name });
          } else {
            if (!this.office?._id) throw new Error("No hay sede seleccionada para editar.");
            res = await updateOffice(this.office._id, { officeName: name });
          }

          const saved = res?.office || res;
          if (!saved || !saved._id) throw new Error(res.error);

          if (this.officeEditor.mode === "create") {
            this.$store.commit("offices/add", saved);
          } else {
            this.$store.commit("offices/update", saved);
          }
          this.officesAll = this.upsertById(this.officesAll, saved);
          this.office = saved;
          this.officeEditor.open = false;

          Swal?.close();
          Swal?.fire({
            icon: "success",
            title: this.officeEditor.mode === "create" ? "Sede creada con éxito" : "Sede actualizada con éxito",
            text: "La sede se ha guardado correctamente.",
            confirmButtonColor: "#bd0909",
          });
        } catch (err) {
          Swal?.close();
          Swal?.fire({ icon: "error", title: "No se pudo guardar", text: err?.response?.data?.error || err?.message || "Inténtalo nuevamente.", confirmButtonColor: "#bd0909" });
        }
      },

      openRoomCreate() {
        this.roomEditor = {
          open: true,
          mode: "create",
          name: "",
          ip: "",
          platformType: null,
          qualification: 0,
        };
      },
      openRoomEdit() {
        this.roomEditor = {
          open: true,
          mode: "edit",
          name: this.room?.roomName || "",
          ip: this.room?.ip || "",
          platformType: this.room?.platformType ?? null,
          qualification: Number(this.room?.qualification) || 0,
        };
      },
      cancelRoomEdit() {
        this.roomEditor.open = false;
      },
      async submitRoom() {
        if (!this.office?._id) return;
        const name = (this.roomEditor.name || "").trim();
        if (!name) return;

        const Swal = this.getSwal();

        if (!this.roomEditor.platformType) {
          Swal?.fire({
            text: "Debes seleccionar el tipo de plataforma (Privada, Free o Ambas).",
            icon: "error",
            confirmButtonColor: "#bd0909",
          });
          return;
        }

        try {
          Swal?.fire({
            title: this.roomEditor.mode === "create" ? "Creando habitación..." : "Actualizando habitación...",
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
          });

          const basePayload = {
            roomName: name,
            ip: (this.roomEditor.ip || "").trim(),
            platformType: this.roomEditor.platformType,
            qualification: Number(this.roomEditor.qualification) || 0,
            officeId: this.office._id,
            officeName: this.office.officeName,
          };

          let res;
          if (this.roomEditor.mode === "create") {
            res = await createRoom(basePayload);
          } else {
            if (!this.room?._id) throw new Error("No hay habitación seleccionada para editar.");
            res = await updateRoom(this.room._id, basePayload);
          }

          const saved = res?.room || res;
          console.log(res);
          if (!saved || !saved._id) throw new Error(res.error);

          if (this.roomEditor.mode === "create") {
            this.$store.commit("rooms/add", saved);
          } else {
            this.$store.commit("rooms/update", saved);
          }
          this.roomsAll = this.upsertById(this.roomsAll, saved);
          this.room = saved;

          this.platformType = saved.platformType ?? null;
          this.qualificationRoom = Number(saved.qualification) || 0;

          this.roomEditor.open = false;

          Swal?.close();
          Swal?.fire({
            icon: "success",
            title: this.roomEditor.mode === "create" ? "Habitación creada con éxito" : "Habitación actualizada con éxito",
            text: "La habitación se ha guardado correctamente.",
            confirmButtonColor: "#bd0909",
          });
        } catch (err) {
          Swal?.close();
          Swal?.fire({
            icon: "error",
            title: "No se pudo guardar",
            text: err?.response?.data?.error || err?.message || "Inténtalo nuevamente.",
            confirmButtonColor: "#bd0909",
          });
        }
      },

      async saveRoom() {
        const Swal = this.getSwal();

        if (!this.platformType) {
          Swal?.fire({ text: "Debes seleccionar al menos una categoría (Free, Privada o Grupal).", icon: "error", confirmButtonColor: "#bd0909" });
          return;
        }
        if (!this.room?._id) return;

        try {
          Swal?.fire({ title: "Guardando...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
          const payload = {
            roomName: (this.room?.roomName || "").trim(),
            platformType: this.platformType,
            qualification: this.qualificationRoom,
            officeId: this.office?._id,
            officeName: this.office?.officeName,
          };

          const res = await updateRoom(this.room._id, payload);

          const saved = res?.room || res;
          if (!saved?._id) throw new Error("La API no devolvió el objeto persistido.");

          this.$store.commit("rooms/update", saved);
          this.roomsAll = this.upsertById(this.roomsAll, saved);
          this.room = saved;

          Swal?.close();
          Swal?.fire({ text: "Cambios guardados correctamente.", icon: "success", confirmButtonColor: "#bd0909" });
        } catch (err) {
          Swal?.close();
          Swal?.fire({ text: err?.response?.data?.error || err?.message || "Error al guardar la habitación", icon: "error", confirmButtonColor: "#bd0909" });
        }
      },
    },

    async mounted() {
      await this.mountedFetches();
    },
  };
</script>

<template>
  <section class="location">
    <h2 class="location__title">Administración de ubicación</h2>

    <section class="location-form">
      <form class="location-form__form" @submit.prevent>
        <CityField
          :cities="cityOptions"
          :value="city"
          :enabled="!!city?.isEnable"
          :inOperation="(city?.cityStatus || 'available') !== 'retired'"
          :editorOpen="cityEditor.open"
          :editorMode="cityEditor.mode"
          @select="onCitySelect"
          @update:enabled="onCityEnable"
          @update:inOperation="onCityOperation"
          @add="openCityCreate"
          @edit="openCityEdit" />
        <div v-if="cityEditor.open" class="location-form__inline-editor">
          <label class="location-form__inline-label">
            {{ cityEditor.mode === "create" ? "Nueva ciudad" : "Editar ciudad" }}
          </label>
          <input class="location-form__inline-input" v-model="cityEditor.name" placeholder="Ciudad" />
          <div class="location-form__inline-actions">
            <button type="button" class="btn btn--ghost" @click="cancelCityEdit">Cancelar</button>
            <button type="button" class="btn btn--primary" @click="submitCity">
              {{ cityEditor.mode === "create" ? "Crear" : "Actualizar" }}
            </button>
          </div>
        </div>

        <OfficeField
          :offices="officeOptions"
          :value="office"
          :enabled="!!office?.isEnable"
          :inOperation="(office?.officeStatus || 'available') !== 'retired'"
          :disabled="!city || locksOffices"
          :editorOpen="officeEditor.open"
          :editorMode="officeEditor.mode"
          @select="onOfficeSelect"
          @update:enabled="onOfficeEnable"
          @update:inOperation="onOfficeOperation"
          @add="openOfficeCreate"
          @edit="openOfficeEdit" />
        <div v-if="officeEditor.open" class="location-form__inline-editor">
          <label class="location-form__inline-label">
            {{ officeEditor.mode === "create" ? "Nueva sede" : "Editar sede" }}
          </label>
          <input class="location-form__inline-input" v-model="officeEditor.name" placeholder="Sede" />
          <div class="location-form__inline-actions">
            <button type="button" class="btn btn--ghost" @click="cancelOfficeEdit">Cancelar</button>
            <button type="button" class="btn btn--primary" @click="submitOffice">
              {{ officeEditor.mode === "create" ? "Crear" : "Actualizar" }}
            </button>
          </div>
        </div>
        <RoomField
          :rooms="roomOptions"
          :value="room"
          :enabled="!!room?.isEnable"
          :inOperation="(room?.roomStatus || 'available') !== 'retired'"
          :disabled="!office || locksRooms"
          :editorOpen="roomEditor.open"
          :editorMode="roomEditor.mode"
          @select="onRoomSelect"
          @update:enabled="onRoomEnable"
          @update:inOperation="onRoomOperation"
          @add="openRoomCreate"
          @edit="openRoomEdit" />
        <div v-if="roomEditor.open" :key="'room-editor-' + (room?._id || 'new')" class="location-form__inline-editor">
          <label class="location-form__inline-label">
            {{ roomEditor.mode === "create" ? "Nueva habitación" : "Editar habitación" }}
          </label>

          <label class="location-form__inline-label"> Nombre: </label>
          <AtomInput class="location-form__inline-input" id="room-name" :value="roomEditor.name" placeholder="Habitación" variant="primary" @input="roomEditor.name = $event.target.value" />

          <label class="location-form__inline-label"> IP: </label>
          <AtomInput class="location-form__inline-input" id="room-ip" :value="roomEditor.ip" placeholder="IP" variant="primary" @input="roomEditor.ip = $event.target.value" />

          <div class="location-form__inline-line">
            <div class="location-form__inline-group">
              <span class="location-form__inline-label">Tipo de categoria:</span>
              <PlatformTypeSelector :subjectType="platformSubject" :value="roomEditor.platformType" @input="roomEditor.platformType = $event" />
            </div>

            <div class="location-form__inline-group">
              <span class="location-form__inline-label">Calificación:</span>
              <StarsRatingInput class="location-form__inline-rating" :value="roomEditor.qualification" @input="roomEditor.qualification = $event" />
            </div>
          </div>

          <div class="location-form__inline-actions">
            <button type="button" class="btn btn--ghost" @click="cancelRoomEdit">Cancelar</button>
            <button type="button" class="btn btn--primary" @click="submitRoom">
              {{ roomEditor.mode === "create" ? "Crear" : "Actualizar" }}
            </button>
          </div>
        </div>
      </form>
    </section>
  </section>
</template>

<style lang="scss" scoped>
  .location {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    &__title {
      width: 100%;
      max-width: 820px;
      margin: 50px 0 20px 0;
      font-size: 24px;
      font-weight: 600;
      color: #bd0909;
      text-align: left;
      font-family: $newFont_bold;
      text-transform: none;
    }
  }

  .location-form {
    width: 100%;
    max-width: 820px;
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 6px #d8d8d8;

    &__form {
      display: flex;
      flex-direction: column;
    }

    &__inline-editor {
      margin-top: 8px;
      padding: 12px;
      border-radius: 6px;
      background: #fafafa;
      border: 1px solid #eee;
    }
    &__inline-label {
      display: block;
      margin-bottom: 6px;
      font-family: $newFont_medium;
      color: #3d3d3d;
    }
    &__inline-input {
      width: 100%;
      height: 36px;
      padding: 6px 10px;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      outline: none;
      font-family: $newFont_regular;
      background-color: #fff;
      margin-bottom: 1rem;
    }
    &__inline-actions {
      margin-top: 8px;
      display: flex;
      gap: 12px;
    }
    &__inline-line {
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: 48px;
      align-items: start;
      margin-top: 14px;
      margin-bottom: 14px;
    }

    &__inline-group {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    &__inline-rating {
      font-size: 35px;
      line-height: 0.4;
    }

    .btn {
      border: none;
      padding: 5px 14px;
      border-radius: 8px;
      cursor: pointer;
      font-family: $newFont_regular;
      &--primary {
        background: #bd0909;
        color: #fff;
      }
      &--ghost {
        background: #e0e0e0;
        color: #333;
      }
    }

    &__save {
      font-family: $newFont_medium;
      margin-top: 6px;
      background: #bd0909;
      color: #fff;
      border: none;
      border-radius: 8px;
      padding: 10px 16px;
      cursor: pointer;
      align-self: flex-end;
    }
  }

  @media (max-width: 860px) {
    .location__title {
      font-size: 18px;
    }
  }
</style>
